import cx from "classnames";
import { uniqueId } from "lodash";
import * as React from "react";
import { expandCollapse } from "./utility/expandCollapse";
import { QESButton } from "./Button";
import { JSX } from "react";

export interface QESAlertProps {
  /**
   * The content of the alert banner, passed as JSX children and not a prop
   */
  children: React.ReactNode;
  /**
   * Optional class names on the alert's outer container div
   */
  classNames?: string;
  /**
   * The title shown on the banner when it is collapsed, like an accordion. Using this prop enables the accordion behavior.
   */
  collapseTitle?: JSX.Element | string;
  /**
   *  An event handler to call when non-persisted alerts are closed. If this is inside an AlertsContainer, the container will pass in this prop.
   */
  onClose?: () => void;
  /**
   * Determines whether the banner has an "x" on the right side to allow the user to close it
   */
  persist?: boolean;
  /**
   * Determines the coloring and icon of the alert banner. The names are borrowed from bootstrap.
   */
  type: "success" | "warning" | "danger" | "info";
}

/**
 * Alert banner component, intended to convey important information to the user. There are four levels: <br>
 *  - "danger" should be used when something unexpected occured, like an error <br>
 *  - "warning" should be used when further action is required, like an update was made and now a project needs to be run<br>
 *  - "info" should be used to convey contextual information <br>
 *  - "success" should be used when an operation completes successfully, though we normally use a toast instead <br>
 *  <br>
 *  To collapse multiple Alerts together, check out `AlertsContainer`.
 */
export const QESAlert = (props: QESAlertProps): JSX.Element => {
  const [show, setShow] = React.useState(true);
  const id = React.useRef(uniqueId("alert-"));
  const collapsedContainerRef = React.useRef<HTMLDivElement>(null);
  const iconRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (!show && props.onClose) {
      props.onClose();
    }
  }, [show]);

  const iconClass = React.useMemo(() => {
    switch (props.type) {
      case "success":
        return "check-circle";
      case "warning":
        return "exclamation-triangle";
      case "danger":
        return "exclamation-circle";
      case "info":
        return "info-circle";
    }
  }, [props.type]);

  if (!show) {
    return null;
  }

  const closeAlert = () => {
    setShow(false);
  };

  return (
    <div
      className={cx({
        "qes-alert": true,
        [props.type]: true,
        [props.classNames]: !!props.classNames,
      })}
      role="alert"
    >
      <div className="qes-alert__icon"><i className={`fa fa-${iconClass}`} /></div>
      {props.collapseTitle !== undefined && props.collapseTitle !== "" ? (
        <>
          <div className="qes-alert__content">
            <QESButton
              qesType="secondary"
              className="qes-alert-expansion-button position-relative p-0 text-dark font-weight-normal text-decoration-none"
              onClick={() => expandCollapse(collapsedContainerRef, () => iconRef.current.style.transform = null, () => iconRef.current.style.transform = "rotateX(180deg)")}
              aria-pressed={collapsedContainerRef.current?.classList.contains("qes-js-open")}
              qesTestId={`alert__collapse-toggle--${props.collapseTitle}`}
            >
              {props.collapseTitle}
              <span className="qes-alert-collapse-indicator"><i className="fa fa-fw fa-chevron-down" ref={iconRef} /></span>
            </QESButton>
            <div className="qes-alert__collapsed" ref={collapsedContainerRef} id={id.current}>
              <div className="qes-alert__collapsed-content">
                {props.children}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="qes-alert__content">
          {props.children}
        </div>
      )}
      {/* There's a decent change this qesTestId won't be unique on a given page, but there's not really any other consistent identifying information here */}
      {!props.persist && (
        <QESButton
          qesType="secondary"
          className="qes-alert__close text-dark border-left border-secondary text-decoration-none"
          onClick={closeAlert}
          qesTestId="alert__close"
        >
          <i className="fa fa-times" />
        </QESButton>
      )}
    </div>
  );
};
